import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import SingleLineEllipsis from '@deltek/specification-client/core/Feedbacks/SingleLineEllipsis'
import { debounce } from '@material-ui/core'
import Chip from '@material-ui/core/Chip'
import { makeStyles } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import Autocomplete, {
    AutocompleteCloseReason,
    AutocompleteGetTagProps,
} from '@material-ui/lab/Autocomplete'
import React, { ChangeEvent, useCallback, useState } from 'react'
import Truncate from 'react-truncate'

const autoCompleteStyles = makeStyles({
    chipLabel: {
        overflow: 'unset',
        textOverflow: 'unset',
        whiteSpace: 'normal',
        maxWidth: 200,
    },
    option: {
        color: '#5E5F61',
    },
})

export interface AutoCompleteFilterProps {
    /** Name of this autocomplete. Used to create its id. */
    name: string

    /** The items the user can choose from. */
    items: string[]

    /** Hint text for textbox. */
    placeholder?: string

    /** Fired when user selects or removes an item. */
    onChange?: (
        event: ChangeEvent<{}>,
        /** The selected items. */
        value: string[]
    ) => void

    /** Fired shortly after the autocomplete input text field changes. */
    onInputChangeDebounced?: (
        /** The value of the input text field. */
        value: string
    ) => void

    /** Fired when user opens the drop-down (e.g., by clicking the textbox,
     * clicking the arrow button, or typing in the textbox when the drop-down is closed). */
    onOpen?: (event: ChangeEvent<{}>) => void

    /** Fired when the user closes the drop-down (e.g., by clicking the arrow button or hitting Esc.) */
    onClose?: (event: ChangeEvent<{}>) => void

    /** Items to show as selected. Each will have a pill with an [x] that
     * can be clicked to remove them from this list.
     */
    selectedItems?: string[]

    /** If displayed pill and on hover are different, we can control it by passing the same set of modified array here.
     * For Family Names, the displayed value in the pill has no sectionNumber compared to the value on hover which has
     */
    selectedDisplayItems?: string[]

    /** Reference element for input textbox. */
    onInputRef?: (inputElement: HTMLElement) => void

    /** If true, show a loading state for the drop-down selection listbox. */
    loading?: boolean

    /** If true, show custom format for option. */
    customFormat?: boolean
}

interface AutoCompleteTagsProps {
    name: string
    customFormat: boolean
    option: string
    index: number
    selectedDisplayItems?: string[]
    getTagProps: AutocompleteGetTagProps
}

const AutoCompleteTags: React.FC<AutoCompleteTagsProps> = ({
    name,
    customFormat,
    option,
    index,
    selectedDisplayItems,
    getTagProps,
}) => {
    const styles = autoCompleteStyles()
    const optionName = customFormat ? customOptionDisplay(option, name) : option
    return (
        <Chip
            {...getTagProps({ index })}
            classes={{ label: styles.chipLabel }}
            variant="outlined"
            label={
                !!selectedDisplayItems && selectedDisplayItems?.length > 0 ? (
                    // Use hover tooltip to always show tooltip not only when truncated
                    <HoverTooltip
                        text={optionName}
                        children={
                            <div>
                                <Truncate lines={1}>
                                    {selectedDisplayItems[index]}
                                </Truncate>
                            </div>
                        }
                    />
                ) : (
                    <SingleLineEllipsis text={optionName} />
                )
            }
        />
    )
}

const customOptionDisplay = (option: string, name: string) => {
    //string = display name - firm name
    const names = option.split('%|%')
    if (names.length > 1 && name === 'filter-company-autocomplete') {
        if (names[0] === names[1]) {
            return names[0].slice(1, -1)
        }
        if (names[0] === 'null') {
            return names[1].slice(1, -1)
        }
        return `${names[0].slice(1, -1)} (${names[1].slice(1, -1)})`
    }
    return option
}

const AutoCompleteFilter: React.FC<AutoCompleteFilterProps> = ({
    name,
    items,
    placeholder,
    onChange,
    onInputChangeDebounced,
    onOpen,
    onClose,
    selectedItems,
    selectedDisplayItems,
    onInputRef,
    loading = false,
    customFormat = false,
    ...props
}) => {
    const styles = autoCompleteStyles()
    let onTextInputRef = onInputRef
    const [open, setOpen] = useState(false)
    const [inputValue, setInputValue] = React.useState('')
    const [loadingOverride, setLoadingOverride] = useState<boolean>(false)
    const selectedValues = new Set<string>(selectedItems)
    const debouncedRaiseOnInput = useCallback(
        debounce((newValue: string) => {
            onInputChangeDebounced?.(newValue)
            setLoadingOverride(false) // we can now wait on the parent component to set 'loading' (e.g., from a useQuery)
        }),
        []
    )

    const handleOpen = useCallback(
        (e) => {
            e.preventDefault()
            setOpen(true)
            onOpen?.(e)
        },
        [onOpen]
    )

    const handleClose = useCallback(
        (e: ChangeEvent<{}>, reason: AutocompleteCloseReason) => {
            e.preventDefault()

            // This prevents closing the dropdown when the tree loads since accordiontree(not a button) takes focus when the tree loads
            if (
                reason === 'blur' && // @ts-expect-error relatedTarget not in changeevent
                e.relatedTarget?.type !== 'button' && // @ts-expect-error relatedTarget not in changeevent
                !e.relatedTarget?.className.includes('Mui') // for closing the select dropdown while retaining the popover open
            ) {
                ;(e.target as HTMLInputElement).focus()
                return
            }

            setOpen(false)

            // This is a flag for the blur to retain the inputvalue. We may need to revisit this if we don't want to clear the input value on blur
            if ((props as any).clearOnBlur === false) {
                setInputValue('')
            }

            onClose?.(e)
        },
        [onClose]
    )

    const handleInputChange = useCallback((_unusedEvent, newInputValue) => {
        // show 'Loading...' as soon as user presses a key; this avoids showing a 'No Results' briefly before 'loading' state
        // is refreshed by parent component (e.g., from a useQuery call)
        setLoadingOverride(true)
        setInputValue(newInputValue)
        debouncedRaiseOnInput(newInputValue)
    }, [])

    const handleFilterOptions = useCallback(
        (options, { inputValue }) =>
            options.filter(
                (option: string) =>
                    // To be included in the drop-down list, an option can't already be selected...
                    !selectedValues.has(option) &&
                    // ...AND it must contain the input search string, if any.
                    (!inputValue ||
                        option
                            .toLocaleLowerCase()
                            .includes(inputValue.toLocaleLowerCase()))
            ),
        [selectedValues]
    )

    const renderOption = useCallback(
        (option: string) => (
            <Typography>
                {customFormat ? customOptionDisplay(option, name) : option}
            </Typography>
        ),
        [customFormat, name, customOptionDisplay]
    )

    const renderTags = useCallback(
        (value, getTagProps) => {
            return value.map((option: string, index: number) => (
                <AutoCompleteTags
                    key={index}
                    name={name}
                    customFormat={customFormat}
                    option={option}
                    index={index}
                    getTagProps={getTagProps}
                    selectedDisplayItems={selectedDisplayItems}
                />
            ))
        },
        [name, customFormat, selectedDisplayItems]
    )

    const renderInput = useCallback(
        (params) => {
            const { InputProps, ...restParams } = params
            const { startAdornment, ...restInputProps } = InputProps
            return (
                <>
                    <input
                        style={{ display: 'none' }}
                        type="text"
                        name={`fake${name}`}
                    />
                    <TextField
                        {...restParams}
                        inputProps={{
                            ...params.inputProps,
                            'aria-autocomplete': 'none',
                            autoComplete: 'off',
                        }}
                        InputProps={{
                            ...restInputProps,
                            startAdornment: (
                                <div
                                    style={{
                                        maxHeight: 266,
                                        overflowY: 'auto', // show vertical scrollbar when many items selected
                                    }}
                                >
                                    {startAdornment}
                                </div>
                            ),
                        }}
                        id={`${name}-input`}
                        data-testid={`${name}-input-test`}
                        name={`input${name}`}
                        label="SELECT"
                        variant="outlined"
                        inputRef={(input) =>
                            onTextInputRef && onTextInputRef(input)
                        }
                        placeholder={placeholder}
                    />
                </>
            )
        },
        [name, placeholder, onTextInputRef]
    )

    return (
        <Autocomplete<string, true, true>
            autoHighlight
            classes={{ option: styles.option }}
            defaultValue={undefined}
            disableClearable
            filterOptions={handleFilterOptions}
            id={`${name}-autocomplete`}
            inputValue={inputValue}
            ListboxProps={{ style: { maxHeight: 260 } }}
            loading={loading || loadingOverride}
            multiple
            onClose={handleClose}
            onChange={onChange}
            onInputChange={handleInputChange}
            onOpen={handleOpen}
            open={open}
            options={items}
            renderInput={renderInput}
            renderOption={renderOption}
            renderTags={renderTags}
            value={selectedItems}
            {...props}
        />
    )
}

export default AutoCompleteFilter
